import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Value } from 'react-phone-number-input';
import { OrganizationData } from '../../services/organization-service';


import { useUpdateOrganization } from '../../hooks/use-organization';
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from 'components/ui/form';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { useTranslation } from 'react-i18next';
import { Textarea } from 'components/ui/textarea';
import { validatePhoneNumber } from 'utils/validation/phone-validation';

const editOrganizationSchema = (t: (key: string) => string) => z.object({
    itemId: z.string(),
    name: z.string().min(1, t('NAME_IS_REQUIRED')),
    email: z.string().email(t('INVALID_EMAIL_ADDRESS')),
    phoneNumber: z.string()
        .refine(
            (val) => !val || /^\+[1-9]\d{1,14}$/.test(val),
            { message: t('INVALID_PHONE_NUMBER') }
        )
        .refine(
            validatePhoneNumber,
            { message: t('PHONE_NUMBER_TOO_SHORT') }
        )
        .optional(),
    websiteUrl: z.string()
        .refine(
            (val) => !val || /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(val),
            { message: t('INVALID_WEBSITE_URL') }
        )
        .optional()
        .or(z.literal('')),
    address: z.string().min(1, t('ADDRESS_IS_REQUIRED')).max(150, t('MAX_150_CHARACTERS')),
});

type FormData = {
    itemId: string;
    name: string;
    email: string;
    phoneNumber?: string;
    websiteUrl?: string;
    address: string;
};

interface EditOrganizationDetailsProps {
    organization: OrganizationData;
    onClose: () => void;
}

export const EditOrganizationDetails: React.FC<EditOrganizationDetailsProps> = ({
    organization,
    onClose,
}) => {
    const { t } = useTranslation();
    const [isFormChanged, setIsFormChanged] = useState(false);

    const form = useForm<FormData>({
        resolver: zodResolver(editOrganizationSchema(t)),
        defaultValues: {
            itemId: '',
            name: '',
            email: '',
            phoneNumber: '',
            websiteUrl: '',
            address: '',
        },
    });

    const { watch, setValue, handleSubmit, control } = form;
    const watchedValues = watch();

    const { mutate: updateOrganization, isPending } = useUpdateOrganization({
        onSuccess: () => {
            onClose();
        },
    });

    useEffect(() => {
        if (organization) {
            setValue('itemId', organization.itemId);
            setValue('name', organization.name);
            setValue('email', organization.email ?? '');
            setValue('phoneNumber', organization.phoneNumber ?? '');
            setValue('websiteUrl', organization.websiteUrl ?? '');
            setValue('address', organization.address ?? '');
        }
    }, [organization, setValue]);

    useEffect(() => {
        if (!organization) return;

        const initialValues = {
            name: organization.name,
            email: organization.email,
            phoneNumber: organization.phoneNumber || '',
            websiteUrl: organization.websiteUrl || '',
            address: organization.address || '',
        };

        setIsFormChanged(
            watchedValues.name !== initialValues.name ||
            watchedValues.email !== initialValues.email ||
            watchedValues.phoneNumber !== initialValues.phoneNumber ||
            watchedValues.websiteUrl !== initialValues.websiteUrl ||
            watchedValues.address !== initialValues.address
        );
    }, [watchedValues, organization]);

    const onSubmit = async (data: FormData) => {
        const { itemId, ...updateData } = data;

        // Check if phone number is only a country code (like +880, +1, +44, etc.)
        const isOnlyCountryCode = updateData.phoneNumber && updateData.phoneNumber.match(/^\+\d{1,4}$/) && updateData.phoneNumber.length <= 5;

        const payload = {
            ...updateData,
            code: "",
            phoneNumber: isOnlyCountryCode ? "" : updateData.phoneNumber || ""
        };
        updateOrganization({ id: itemId, data: payload });
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <DialogContent
            className="rounded-md sm:max-w-[700px] overflow-y-auto max-h-screen"
            onClick={handleDialogClick}
        >
            <DialogHeader>
                <DialogTitle>{t('EDIT_ORGANIZATION_DETAILS')}</DialogTitle>
                <DialogDescription>{t('KEEP_DETAILS_ACCURATE_UP_TO_DATE')}</DialogDescription>
            </DialogHeader>
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                            control={control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="name">{t('ORGANIZATION_NAME')} *</Label>
                                    <FormControl>
                                        <Input id="name" placeholder={t('ENTER_ORGANIZATION_NAME')} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="email">{t('EMAIL')} *</Label>
                                    <FormControl>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder={t('ENTER_EMAIL')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="phoneNumber">{t('PHONE_NUMBER')}</Label>
                                    <FormControl>
                                        <UIPhoneInput
                                            value={field.value as Value}
                                            onChange={(value: Value) => setValue('phoneNumber', value as string)}
                                            placeholder={t('ENTER_PHONE_NUMBER')}
                                            defaultCountry="CH"
                                            countryCallingCodeEditable={false}
                                            international
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="websiteUrl"
                            render={({ field }) => (
                                <FormItem>
                                    <Label htmlFor="websiteUrl">{t('WEBSITE')}</Label>
                                    <FormControl>
                                        <Input
                                            id="websiteUrl"
                                            placeholder={t('ENTER_WEBSITE_URL')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={control}
                        name="address"
                        render={({ field }) => (
                            <FormItem>
                                <Label htmlFor="address">{t('ADDRESS')} *</Label>
                                <FormControl>
                                    <Textarea
                                        id="address"
                                        placeholder={t('ENTER_ADDRESS')}
                                        className="resize-none"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="w-full sm:w-auto"
                            disabled={isPending}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={!isFormChanged || isPending}
                            className="w-full sm:w-auto"
                        >
                            {isPending ? t('SAVING') : t('SAVE')}
                        </Button>
                    </DialogFooter>
                </form>
            </Form>
        </DialogContent>
    );
}; 